import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { PaginationMeta } from '@/modules/auth/types';

const ENDPOINT = '/api/v1/billing';
const KEY = ['billing'];

export type BillingOrderStatus = 'PENDING' | 'PAID' | 'CANCELLED';

export interface BillingOrder {
  id: string;
  lineId: string | null;
  planName: string | null;
  planAmount: number;
  lineAmount: number;
  creditAmount: number;
  messages: number;
  total: number;
  status: BillingOrderStatus;
  checkoutUrl: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface CheckoutInput {
  lineId?: string;
  messages: number;
}

export interface MessagePack {
  messages: number;
  price: number;
}

export interface MessagePacks {
  messagePrice: number | null;
  data: MessagePack[];
}

export interface BillingOrders {
  data: BillingOrder[];
  meta: PaginationMeta;
}

export interface LicensePlan {
  id: string;
  name: string;
  description: string | null;
  monthlyPrice: number;
  calculatedMonthlyPrice: number | null;
  introPrice: number | null;
  introMonths: number;
  maxUnits: number | null;
  maxUsers: number | null;
  includedMessages: number | null;
  isActive: boolean;
  businessMultiplier: number;
  features: string[];
  businessFeatures: string[];
  whatsappBot: boolean;
  whatsappMarketing: boolean;
  ctaLabel: string | null;
  highlighted: boolean;
  order: number;
}

/** Campos que a tela de admin edita - todos opcionais, so manda o que mudou. */
export interface PlanInput {
  name?: string;
  description?: string | null;
  monthlyPrice?: number;
  introPrice?: number | null;
  introMonths?: number;
  maxUnits?: number | null;
  maxUsers?: number | null;
  includedMessages?: number | null;
  isActive?: boolean;
  businessMultiplier?: number;
  features?: string[];
  businessFeatures?: string[];
  whatsappBot?: boolean;
  whatsappMarketing?: boolean;
  ctaLabel?: string | null;
  highlighted?: boolean;
  order?: number;
}

export interface LicenseLine {
  id: string;
  phoneNumber: string;
  unitName: string | null;
  monthlyPrice: number;
  status: string;
  paidThrough: string | null;
}

export interface License {
  plan: LicensePlan | null;
  startedAt: string | null;
  usage: {
    units: number;
    users: number;
    creditBalance: number;
    messagesLeft: number | null;
  };
  lines: LicenseLine[];
  paidTotal: number;
  openTotal: number;
}

export function useLicense() {
  return useQuery({
    queryKey: [...KEY, 'license'],
    queryFn: () => apiClient.get<License>(`${ENDPOINT}/license`),
  });
}

export function useMessagePacks() {
  return useQuery({
    queryKey: [...KEY, 'message-packs'],
    queryFn: () => apiClient.get<MessagePacks>(`${ENDPOINT}/message-packs`),
  });
}

export function usePlans() {
  return useQuery({
    queryKey: [...KEY, 'plans'],
    queryFn: () => apiClient.get<{ data: LicensePlan[] }>(`${ENDPOINT}/plans`),
  });
}

export function useSetPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string | null) =>
      apiClient.put<License>(`${ENDPOINT}/license/plan`, { planId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useOrders(page: number) {
  return useQuery({
    queryKey: [...KEY, 'orders', page],
    queryFn: () => apiClient.get<BillingOrders>(`${ENDPOINT}/orders?page=${page}&perPage=20`),
  });
}

export function useOpenOrder(lineId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'open', lineId],
    enabled: Boolean(lineId),
    queryFn: () =>
      apiClient.get<{ order: BillingOrder | null }>(`${ENDPOINT}/orders/open?lineId=${lineId}`),
  });
}

function useBillingMutation<TArgs>(mutationFn: (args: TArgs) => Promise<BillingOrder>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
      queryClient.invalidateQueries({ queryKey: ['phone-lines'] });
      queryClient.invalidateQueries({ queryKey: ['credits'] });
    },
  });
}

export function useCheckout() {
  return useBillingMutation((input: CheckoutInput) =>
    apiClient.post<BillingOrder>(`${ENDPOINT}/checkout`, input),
  );
}

export function useSyncOrder() {
  return useBillingMutation((id: string) =>
    apiClient.post<BillingOrder>(`${ENDPOINT}/orders/${id}/sync`),
  );
}
